import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { salesOrdersApi as SO, inr } from "@/lib/salesOrders";
import { quotationsApi } from "@/lib/quotations";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";

export default function SalesOrderForm() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const quotationParam = sp.get("quotation");

  const [meta, setMeta] = useState({ priorities: [], shipping_methods: [] });
  const [quote, setQuote] = useState(null);   // source quotation (for convert) or inherited data (edit)
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    quotation_id: "", priority: "Normal", delivery_date: "", shipping_method: "",
    shipping_address: {}, payment_terms: "", remarks: "", production_notes: "", customer_instructions: "",
  });

  useEffect(() => {
    (async () => {
      const m = await SO.meta(); setMeta(m);
      if (editing) {
        const so = await SO.get(id);
        if (so.status !== "Draft") { toast.error("Only Draft orders can be edited."); navigate(`/sales-orders/${id}`); return; }
        setQuote(so);
        setF({
          quotation_id: so.quotation_id, priority: so.priority || "Normal", delivery_date: so.delivery_date || "",
          shipping_method: so.shipping_method || "", shipping_address: so.shipping_address || {},
          payment_terms: so.payment_terms || "", remarks: so.remarks || "",
          production_notes: so.production_notes || "", customer_instructions: so.customer_instructions || "",
        });
      } else if (quotationParam) {
        try {
          const q = await quotationsApi.get(quotationParam);
          if (q.status !== "Accepted") { toast.error("Only Accepted quotations can be converted."); navigate(`/quotations/${q.id}`); return; }
          if (q.sales_order_id) { toast.info("This quotation is already converted."); navigate(`/sales-orders/${q.sales_order_id}`); return; }
          setQuote(q);
          setF((p) => ({ ...p, quotation_id: q.id, payment_terms: q.customer?.payment_terms || "",
            shipping_address: q.customer?.billing_address || {} }));
        } catch { toast.error("Quotation not found"); navigate("/quotations"); }
      } else { navigate("/quotations"); return; }
      setLoading(false);
    })();
  }, [id, editing, quotationParam, navigate]);

  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const res = editing
        ? await SO.update(id, { priority: f.priority, delivery_date: f.delivery_date || null, shipping_method: f.shipping_method,
            shipping_address: f.shipping_address, payment_terms: f.payment_terms, remarks: f.remarks,
            production_notes: f.production_notes, customer_instructions: f.customer_instructions })
        : await SO.create({ quotation_id: f.quotation_id, priority: f.priority, delivery_date: f.delivery_date || null,
            shipping_method: f.shipping_method, shipping_address: f.shipping_address, payment_terms: f.payment_terms,
            remarks: f.remarks, production_notes: f.production_notes, customer_instructions: f.customer_instructions });
      toast.success(editing ? "Sales order updated" : "Sales order created");
      navigate(`/sales-orders/${res.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  if (loading || !quote) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;

  const c = quote.customer || {};
  const items = quote.items || [];

  return (
    <div className="space-y-6" data-testid="sales-order-form-page">
      <button onClick={() => navigate(editing ? `/sales-orders/${id}` : "/quotations")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="h-4 w-4" />Back</button>
      <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{editing ? "Edit Sales Order" : "New Sales Order"}</h2>
      <p className="-mt-3 text-sm text-indigo-600">{editing ? quote.so_no : "Converting from"} quotation {quote.quote_no}</p>

      <Card className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-heading font-semibold text-slate-900">Inherited from quotation (read-only)</h3>
          <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">Referenced — not editable</Badge>
        </div>
        <div className="grid gap-4 text-sm md:grid-cols-3">
          <div><p className="text-slate-500">Customer</p><p className="font-medium text-slate-800">{c.company_name}</p><p className="text-slate-500">{c.gstin || ""}</p></div>
          <div><p className="text-slate-500">Artwork</p><p className="font-medium text-slate-800">{quote.artwork ? `${quote.artwork.artwork_code} · ${quote.artwork.name} (V${quote.artwork.version})` : "None attached"}</p></div>
          <div><p className="text-slate-500">Grand total</p><p className="font-medium text-slate-800">{inr(quote.totals?.grand_total)}</p></div>
        </div>
        <div className="mt-4 overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase text-slate-500">Fabric / Print</TableHead>
              <TableHead className="text-xs uppercase text-slate-500">Specs</TableHead>
              <TableHead className="text-xs uppercase text-slate-500">Qty</TableHead>
              <TableHead className="text-xs uppercase text-slate-500">Total</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((it, i) => (
                <TableRow key={it.id || i}>
                  <TableCell className="font-medium text-slate-800">{it.fabric_type}<span className="block text-xs text-slate-400">{it.print_type}</span></TableCell>
                  <TableCell className="text-slate-600">{[it.gsm && `${it.gsm} GSM`, it.width].filter(Boolean).join(" · ") || "—"}</TableCell>
                  <TableCell className="text-slate-600">{it.quantity} {it.unit}</TableCell>
                  <TableCell className="tabular-nums font-medium text-slate-800">{inr(it.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-6">
          <h3 className="mb-4 font-heading font-semibold text-slate-900">Order details</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Priority</Label>
              <Select value={f.priority} onValueChange={(v) => upd("priority", v)}>
                <SelectTrigger data-testid="so-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.priorities.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Delivery date</Label><Input type="date" value={f.delivery_date} onChange={(e) => upd("delivery_date", e.target.value)} data-testid="so-delivery-input" /></div>
            <div className="space-y-1.5"><Label>Shipping method</Label>
              <Select value={f.shipping_method || ""} onValueChange={(v) => upd("shipping_method", v)}>
                <SelectTrigger data-testid="so-shipping-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{meta.shipping_methods.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Payment terms</Label><Input value={f.payment_terms} onChange={(e) => upd("payment_terms", e.target.value)} data-testid="so-payment-input" /></div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="mb-4 font-heading font-semibold text-slate-900">Shipping address</h3>
          <div className="grid grid-cols-2 gap-2">
            <Input className="col-span-2" placeholder="Address line 1" value={f.shipping_address?.line1 || ""} onChange={(e) => upd("shipping_address", { ...f.shipping_address, line1: e.target.value })} />
            <Input placeholder="City" value={f.shipping_address?.city || ""} onChange={(e) => upd("shipping_address", { ...f.shipping_address, city: e.target.value })} />
            <Input placeholder="State" value={f.shipping_address?.state || ""} onChange={(e) => upd("shipping_address", { ...f.shipping_address, state: e.target.value })} />
            <Input placeholder="Pincode" value={f.shipping_address?.pincode || ""} onChange={(e) => upd("shipping_address", { ...f.shipping_address, pincode: e.target.value })} />
          </div>
        </Card>

        <Card className="p-6"><h3 className="mb-3 font-heading font-semibold text-slate-900">Order remarks</h3>
          <Textarea rows={3} value={f.remarks} onChange={(e) => upd("remarks", e.target.value)} placeholder="Order-specific remarks" data-testid="so-remarks-input" /></Card>
        <Card className="p-6"><h3 className="mb-3 font-heading font-semibold text-slate-900">Production notes (internal)</h3>
          <Textarea rows={3} value={f.production_notes} onChange={(e) => upd("production_notes", e.target.value)} placeholder="Instructions for the factory" /></Card>
        <Card className="p-6 lg:col-span-2"><h3 className="mb-3 font-heading font-semibold text-slate-900">Customer instructions</h3>
          <Textarea rows={2} value={f.customer_instructions} onChange={(e) => upd("customer_instructions", e.target.value)} placeholder="Any special instructions from the customer" /></Card>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate(editing ? `/sales-orders/${id}` : "/quotations")}>Cancel</Button>
        <Button onClick={save} disabled={saving} data-testid="save-so-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{editing ? "Save changes" : "Create sales order"}
        </Button>
      </div>
    </div>
  );
}
